import React, { useState, useEffect, useCallback } from 'react';
import { DigisacService } from '../../services/digisacService';
import { useNotifier } from '../ui/Notifier';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import type { DigisacSchedule } from '../../types/digisac';

interface ScheduleTableProps {
  refreshKey?: number;
  onCancelled?: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  cancelled: 'Cancelado',
  failed: 'Falhou',
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#F59E0B',
  sent: '#10B981',
  cancelled: '#9CA3AF',
  failed: '#EF4444',
};

export const ScheduleTable: React.FC<ScheduleTableProps> = ({ refreshKey, onCancelled }) => {
  const notifier = useNotifier();
  const [schedules, setSchedules] = useState<DigisacSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DigisacService.listSchedules();
      const list = Array.isArray(data) ? data : (data as any)?.data ?? [];
      setSchedules(list);
    } catch (err: any) {
      notifier.show({ message: err?.message ?? 'Erro ao carregar agendamentos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notifier]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleCancel = (id: string) => {
    setCancelTargetId(id);
    setCancelConfirmOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTargetId) return;
    setCancelConfirmOpen(false);
    setCancelling(cancelTargetId);
    try {
      await DigisacService.cancelSchedule(cancelTargetId);
      notifier.show({ message: 'Agendamento cancelado', severity: 'success' });
      onCancelled?.();
      await load();
    } catch (err: any) {
      notifier.show({ message: err?.message ?? 'Erro ao cancelar agendamento', severity: 'error' });
    } finally {
      setCancelling(null);
      setCancelTargetId(null);
    }
  };

  if (loading) {
    return <p className="text-xs text-gray-500 py-4 text-center">Carregando agendamentos...</p>;
  }

  if (schedules.length === 0) {
    return <p className="text-xs text-gray-400 py-4 text-center">Nenhum agendamento cadastrado.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b" style={{ color: '#3d2b56' }}>
            <th className="text-left py-2 px-3 font-semibold">Telefone</th>
            <th className="text-left py-2 px-3 font-semibold">Tipo</th>
            <th className="text-left py-2 px-3 font-semibold">Template / Mensagem</th>
            <th className="text-left py-2 px-3 font-semibold">Agendado para</th>
            <th className="text-left py-2 px-3 font-semibold">Status</th>
            <th className="text-left py-2 px-3 font-semibold">Ações</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map(sched => (
            <tr key={sched.id} className="border-b hover:bg-gray-50">
              <td className="py-2 px-3 font-mono">{sched.contact?.phoneE164 ?? '—'}</td>
              <td className="py-2 px-3 capitalize">{sched.tipo}</td>
              <td className="py-2 px-3 max-w-xs truncate" title={sched.templateName ?? sched.conteudo ?? ''}>
                {sched.templateName ?? sched.conteudo ?? '—'}
              </td>
              <td className="py-2 px-3 text-gray-600">
                {new Date(sched.agendadoPara).toLocaleString('pt-BR')}
              </td>
              <td className="py-2 px-3">
                <span
                  className="px-2 py-0.5 rounded-full text-white text-xs"
                  style={{ backgroundColor: STATUS_COLORS[sched.status] ?? '#9CA3AF' }}
                >
                  {STATUS_LABELS[sched.status] ?? sched.status}
                </span>
              </td>
              <td className="py-2 px-3">
                {sched.status === 'pending' && (
                  <button
                    onClick={() => handleCancel(sched.id)}
                    disabled={cancelling === sched.id}
                    className="text-xs px-2 py-1 rounded text-white disabled:opacity-50"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    {cancelling === sched.id ? '...' : 'Cancelar'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancelar Agendamento"
        message="Cancelar este agendamento no Digisac?"
        confirmText="Cancelar Agendamento"
        cancelText="Voltar"
        confirmButtonColor="red"
        onConfirm={handleConfirmCancel}
        onCancel={() => { setCancelConfirmOpen(false); setCancelTargetId(null); }}
      />
    </div>
  );
};
